import React, { useEffect, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import Wahanda from 'common/wahanda';
import { Reconnect } from 'src/federation/Reconnect';
import PATHS from './paths';

// Experiment flag read: also read in the Reconnect root, see
// useRebookReminderVersion. Same condition: flag on and messaging credits.
// Remove together with the other reads when the experiment ends.
const REBOOK_REMINDER_EXPERIMENT_FEATURE = 'CCM-1984-rebook-reminder-experiment';

type Destination = 'pending' | 'performance' | 'rebook-reminder-tab';

const useDestination = (venueId: number | string): Destination => {
  const isExperimentOn = Wahanda.Features.isEnabled(REBOOK_REMINDER_EXPERIMENT_FEATURE);
  const [hasMessagingCredits, setHasMessagingCredits] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!isExperimentOn) {
      return undefined;
    }
    let isCancelled = false;
    fetch(`/api/messaging-orchestrator/venue/${venueId}/feature-settings`)
      .then((response) =>
        response.ok ? response.json() : { messagingCreditsFeatureEnabled: false },
      )
      .then((data) => !isCancelled && setHasMessagingCredits(!!data.messagingCreditsFeatureEnabled))
      // Fail closed: keep showing the performance page.
      .catch(() => !isCancelled && setHasMessagingCredits(false));
    return () => {
      isCancelled = true;
    };
  }, [isExperimentOn, venueId]);

  if (!isExperimentOn) {
    return 'performance';
  }
  if (hasMessagingCredits === undefined) {
    return 'pending';
  }
  return hasMessagingCredits ? 'rebook-reminder-tab' : 'performance';
};

export const RebookReminderPerformanceRoute = ({ venueId }: { venueId: number | string }) => {
  const { search } = useLocation();
  const destination = useDestination(venueId);

  if (destination === 'pending') {
    return null;
  }
  if (destination === 'rebook-reminder-tab') {
    // Keeps the query (source, open_buy_credits) so tracking and deep links survive.
    return <Redirect to={{ pathname: PATHS.AUTOMATED_MESSAGING, search }} />;
  }
  return <Reconnect name="RebookReminderPerformancePage" props={{}} />;
};
